import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { NotebookPen } from "lucide-react";

export const LessonNotes = ({ moduleId, lessonIndex }) => {
  const [text, setText] = useState("");
  const [status, setStatus] = useState("loading"); // loading | saved | saving | error
  const timer = useRef(null);
  const pending = useRef(null); // {moduleId, lessonIndex, text} while a save is owed

  const save = (silent = false) => {
    clearTimeout(timer.current);
    const p = pending.current;
    if (!p) return;
    pending.current = null;
    const req = api.put(`/notes/${p.moduleId}/${p.lessonIndex}`, { text: p.text });
    if (silent) req.catch(() => {});
    else req.then(() => setStatus("saved")).catch(() => setStatus("error"));
  };

  useEffect(() => {
    setStatus("loading");
    api.get(`/notes/${moduleId}/${lessonIndex}`)
      .then(r => { setText(r.data.text || ""); setStatus("saved"); })
      .catch(() => setStatus("error"));
    return () => save(true); // flush any pending edit for the previous lesson
  }, [moduleId, lessonIndex]);

  const onChange = (e) => {
    const value = e.target.value;
    setText(value);
    setStatus("saving");
    pending.current = { moduleId, lessonIndex, text: value };
    clearTimeout(timer.current);
    timer.current = setTimeout(() => save(), 800);
  };

  return (
    <div className="mt-10 border hairline bg-white" data-testid="lesson-notes">
      <div className="px-4 py-3 border-b hairline flex items-center justify-between">
        <div className="label-eyebrow flex items-center gap-2">
          <NotebookPen size={13} className="text-[var(--brand-primary)]" /> My notes
        </div>
        <span className="text-xs text-neutral-400 font-mono" data-testid="lesson-notes-status">
          {status === "saving" ? "Saving…" : status === "saved" ? "Saved" : status === "error" ? "Save failed — keep a copy" : ""}
        </span>
      </div>
      <textarea
        value={text}
        onChange={onChange}
        disabled={status === "loading"}
        maxLength={20000}
        placeholder="Jot down key phrases, scripts and takeaways while you read or listen — saved automatically to your account."
        className="w-full min-h-[140px] p-4 text-sm bg-transparent focus:outline-none resize-y"
        data-testid="lesson-notes-input"
      />
    </div>
  );
};
